import MarketList from '@/components/modules/MarketList';
import { Highlights, MarketInstrument } from '@/components/modules/MarketList/UserMarket';
import { isRoundedPlatform, roundedFullClassname } from '@/helpers/environmentHelper';
import { useAppSelector } from '@/state/hooks';
import { PairMap } from '@/state/reducers/marketPairSlice';
import useActivePair from '@/utils/hooks/useActivePair';
import { useClickOutside } from '@/utils/hooks/useClickOutside';
import { useDidUpdate } from '@/utils/hooks/useDidUpdate';
import { useDisclosure } from '@/utils/hooks/useDisclosure';
import { GetColorIndexesReturn, useInstrument } from '@/utils/hooks/useInstrument';
import usePreviousSelector from '@/utils/hooks/usePreviousSelector';
import cn from 'classnames';
import { Fragment, useMemo, useState } from 'react';
import { MdExpandLess, MdExpandMore } from 'react-icons/md';

export const subHeaderClassname = cn(
    'flex items-center justify-between w-full text-sm p-2 px-1.5 cursor-pointer bg-brand-background hover:bg-brand-background-dark',
    { 'mx-3': isRoundedPlatform },
    roundedFullClassname
);

const MobileSubHeader = () => {
    const activePair = useActivePair();
    const marketListDisclosure = useDisclosure(false);
    const ref = useClickOutside(marketListDisclosure[1].close);

    return (
        <div
            ref={ref}
            className={cn(
                'flex lg:hidden relative w-full border-x-[1.5px] border-brand-border border-b-0 h-10 text-brand-text',
                roundedFullClassname
            )}>
            <div
                className={subHeaderClassname}
                onClick={() => {
                    if (activePair?.celer) {
                        marketListDisclosure[1].toggle();
                    }
                }}>
                {activePair?.celer && (
                    <Fragment>
                        <div className="flex items-center gap-5">
                            <MarketInstrument market={activePair} isMobileSubHeader />
                            <CelerPrice market={activePair} />
                        </div>
                        {marketListDisclosure[0] ? (
                            <MdExpandLess className="h-5 w-5" />
                        ) : (
                            <MdExpandMore className="h-5 w-5" />
                        )}
                    </Fragment>
                )}
            </div>
            {marketListDisclosure[0] && (
                <div
                    style={{ width: 'calc(100% - 1px)' }}
                    className={cn(
                        'pop-up-market-list absolute top-[42px] left-[0.5px] z-10 bg-brand-background overflow-clip',
                        roundedFullClassname
                    )}>
                    <MarketList onClick={marketListDisclosure[1].close} />
                </div>
            )}
        </div>
    );
};

const CelerPrice = ({ market }: { market: PairMap }) => {
    const activePair = useActivePair();

    const priceBooksMidPrice = useAppSelector((state) => state.celerMarket.priceBooks[activePair?.celer]?.midprice);
    const prevPriceBooksMidPrice = usePreviousSelector(
        (state) => state.celerMarket.priceBooks[activePair?.celer]?.midprice
    ) as number;

    const [priceHighlights, setPriceHighlights] = useState<GetColorIndexesReturn | undefined>();

    const { formatPip, formatHighlights } = useInstrument(market.celer);

    // currently displaying close
    const midPrice = useMemo(() => priceBooksMidPrice && formatPip(priceBooksMidPrice), [priceBooksMidPrice]);

    // // for highlighting changes based on prev price
    useDidUpdate(() => {
        if (priceBooksMidPrice && prevPriceBooksMidPrice)
            setPriceHighlights(formatHighlights(prevPriceBooksMidPrice, priceBooksMidPrice, priceHighlights));
    }, [priceBooksMidPrice]);

    return (
        <div className="flex items-center gap-2">
            {priceBooksMidPrice > 0 && midPrice && (
                <span>
                    <Highlights ticker={midPrice} highlights={priceHighlights} />
                </span>
            )}
        </div>
    );
};

export default MobileSubHeader;
